import { Component, Input, OnChanges, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Site } from './screens.types';

@Component({
  selector: 'app-comm-patient-screen',
  standalone: true,
  imports: [FormsModule, TranslatePipe],
  templateUrl: './comm-patient-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./comm-patient-screen.component.scss'],
})
export class CommPatientScreenComponent implements OnChanges {
  @Input({ required: true }) site!: Site;

  private readonly defaultWait: Record<string, number> = {
    critique: 45,
    moderee: 20,
    fluide: 8,
    ferme: 0,
  };

  private readonly defaultMessage: Record<string, string> = {
    critique: 'Activité très soutenue en ce moment. Le temps d\'attente est élevé. Merci pour votre patience.',
    moderee: 'Votre prise en charge est en cours. Le temps d\'attente estimé est de 20 min.',
    fluide: 'Le site est disponible et vous accueille sans délai.',
    ferme: 'Le site est actuellement fermé. Merci de vous orienter vers le site le plus proche.',
  };

  currentMessage = '';
  readonly sent = signal(false);

  // ── Temps d'attente ──
  readonly waitMinutes = signal(0);
  readonly waitSaved = signal(false);
  private waitSavedTimer: any;

  readonly waitLevel = computed<'fluide' | 'moderee' | 'critique'>(() => {
    const m = this.waitMinutes();
    if (m <= 15) return 'fluide';
    if (m <= 30) return 'moderee';
    return 'critique';
  });

  readonly waitRingColor = computed(() => {
    const l = this.waitLevel();
    if (l === 'fluide')  return '#179B53';
    if (l === 'moderee') return '#F1B54A';
    return '#EF4444';
  });

  readonly waitTrackColor = computed(() => {
    const l = this.waitLevel();
    if (l === 'fluide')  return '#E9F4ED';
    if (l === 'moderee') return '#FFF1DC';
    return '#FDE6E6';
  });

  readonly waitMinutesColor = computed(() => {
    const l = this.waitLevel();
    if (l === 'fluide')  return '#14532D';
    if (l === 'moderee') return '#5C3B09';
    return '#7F1D1D';
  });

  readonly waitDashOffset = computed(() => {
    return Math.max(0, 100 - (this.waitMinutes() / 90) * 100);
  });

  setWait(event: Event) {
    this.waitMinutes.set(+(event.target as HTMLInputElement).value);
  }

  adjustWait(delta: number) {
    this.waitMinutes.update(v => Math.max(0, Math.min(90, v + delta)));
  }

  saveWait() {
    this.waitSaved.set(true);
    clearTimeout(this.waitSavedTimer);
    this.waitSavedTimer = setTimeout(() => this.waitSaved.set(false), 2500);
  }

  ngOnChanges() {
    this.waitMinutes.set(this.defaultWait[this.site.statut] ?? 20);
    this.currentMessage = this.defaultMessage[this.site.statut] ?? '';
  }

  send() {
    this.sent.set(true);
    setTimeout(() => this.sent.set(false), 2500);
  }

  tensionKey(): string {
    return { critique: 'critique', moderee: 'tension', fluide: 'fluide', ferme: 'fluide' }[this.site.statut];
  }

  tensionLabel(): string {
    return { critique: 'Critique', moderee: 'En tension', fluide: 'Fluide', ferme: 'Fermé' }[this.site.statut];
  }
}